import { AccessTime, KeyboardArrowDownOutlined as KeyboardArrowDownOutlinedIcon } from '@mui/icons-material';
import { Box, Grid, Menu, MenuItem, Snackbar, Typography, Alert } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TimePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import dayjs from 'dayjs';
import React, { Fragment, useEffect } from 'react';
import CustomDatePicker from '../CustomDatePicker/CustomDatePicker';
import DoDisturbOnIcon from '@mui/icons-material/DoDisturbOn';
import SimpleSwitch from '../admin/common/OZSwitch/SimpleSwitch';
import StyledTooltip from '../SubUserChat/components/chat/StyledTooltip';
import { Info } from 'lucide-react';

dayjs.extend(utc);
dayjs.extend(tz);

const useStyles: any = makeStyles(() => ({
    dropdownMenu: {
        minWidth: 'fit-content !important',
        border: '1px solid #e6e5e6 !important',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.04) !important',
        borderRadius: '12px !important',
        marginTop: '10px !important'
    },
    dropdownMenuItem: {
        fontWeight: '400 !important',
        fontSize: '12px !important',
        lineHeight: '14px !important',
        color: '#212121 !important'
    },
    arrowIconContainer: {
        backgroundColor: '#d6d6d7',
        width: '30px',
        border: '1px solid #d6d6d7',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    timePickerMenu: {
        width: '350px !important'
    },
    singleDatePickerMenu: {
        width: '300px !important'
    },
    alignAccestime: {
        paddingTop: '10px',
        paddingLeft: '10px',
        paddingBottom: '5px'
    },
    todayTypography: {
        color: '#3D8BF8 !important',
        paddingLeft: '5px'
    }
}));

type durationobject = {
    fromTime: string;
    toTime: string;
};

interface DurationSelectorProps {
    duration?: string;
    handleDuration?(duration?: string, time?: durationobject): void;
    enableTodayDuration?: boolean;
    enableLastThreeDaysDuration?: boolean;
    enableSameDayLastWeek?: boolean;
    withTimePicker?: boolean;
    allowTodayDateInCustomDate?: boolean;
    allowFutureDates?: boolean;
    allowSingleCustomDate?: boolean;
    enableToday?: boolean;
    showRealTimeToggle?: boolean;
    realtime?: boolean;
    handleClickRealTime?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const DurationSelector = ({
    duration,
    handleDuration,
    enableTodayDuration,
    enableLastThreeDaysDuration,
    enableSameDayLastWeek,
    withTimePicker,
    allowTodayDateInCustomDate,
    allowFutureDates,
    allowSingleCustomDate,
    enableToday,
    showRealTimeToggle = false,
    realtime = false,
    handleClickRealTime
}: DurationSelectorProps) => {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [customAnchorEl, setCustomAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const customOpen = Boolean(customAnchorEl);
    const [startTime, setStartTime] = React.useState<Dayjs>(dayjs().tz(localStorage.getItem('userTZ') || '').startOf('day'));
    const [endTime, setEndTime] = React.useState<Dayjs>(dayjs().tz(localStorage.getItem('userTZ') || ''));
    const [snackbarMessage, setSnackbarMessage] = React.useState('');
    const [openSnackbar, setOpenSnackbar] = React.useState(false);

    const onStartTimeChange = (time: Dayjs | any) => {
        if (endTime && time > endTime) {
            setSnackbarMessage('Start time should be lesser than end time');
            setOpenSnackbar(true);
            return;
        } else {
            setStartTime(time);
            handleSelectDuration('Today', { newStartTime: time });
        }
    };

    const onEndTimeChange = (time: Dayjs | any) => {
        if (time < startTime) {
            setSnackbarMessage('End time should be greater than start time');
            setOpenSnackbar(true);
            return;
        } else {
            setEndTime(time);
            handleSelectDuration('Today', { newEndTime: time });
        }
    };

    const handleRefreshTimeChange = () => {
        const startTimeRefresh = dayjs().tz(localStorage.getItem('userTZ') || '').startOf('day');
        const EndTimeRefresh = dayjs().tz(localStorage.getItem('userTZ') || '');
        setStartTime(startTimeRefresh);
        setEndTime(EndTimeRefresh);
        handleSelectDuration('Today', { newStartTime: startTimeRefresh, newEndTime: EndTimeRefresh });
    };

    const handleSelectDuration = (duration: any, { newStartTime = startTime, newEndTime = endTime } = {}) => {
        let fromTime = '';
        let toTime = '';
        switch (duration) {
            case 'Today':
                if (allowSingleCustomDate) {
                    fromTime = newStartTime?.format('YYYY-MM-DD');
                    toTime = newEndTime?.format('YYYY-MM-DD');
                } else {
                    fromTime = newStartTime?.format('YYYY-MM-DD HH:mm:ss');
                    toTime = newEndTime?.format('YYYY-MM-DD HH:mm:ss');
                }
                break;

            case 'Yesterday':
                const now = new Date();
                const yesterday = new Date(now);
                yesterday.setDate(now.getDate() - 1);
                yesterday.setHours(0, 0, 0, 0);

                fromTime = yesterday.getFullYear() + '-' + (yesterday.getMonth() + 1).toString().padStart(2, '0') + '-' + yesterday.getDate().toString().padStart(2, '0') + ' 00:00:00';
                toTime = yesterday.getFullYear() + '-' + (yesterday.getMonth() + 1).toString().padStart(2, '0') + '-' + yesterday.getDate().toString().padStart(2, '0') + ' 23:59:59';
                break;

            case 'Same day last week':
                const sameDayLastWeek = new Date(new Date().setDate(new Date().getDate() - 7));
                fromTime = sameDayLastWeek.toISOString().split('T')[0] + ' 00:00:00';
                toTime = sameDayLastWeek.toISOString().split('T')[0] + ' 23:59:59';
                break;

            case 'Last 7 Days':
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const todayEnd = new Date(today);
                todayEnd.setHours(23, 59, 59, 999);

                const fromTimeDate = new Date(todayEnd);
                fromTimeDate.setDate(fromTimeDate.getDate() - 7);
                fromTime = fromTimeDate.toISOString().split('T')[0] + ' 00:00:00';

                const yesterdayEnd = new Date(todayEnd);
                yesterdayEnd.setDate(yesterdayEnd.getDate() - 1);
                toTime = yesterdayEnd.toISOString().split('T')[0] + ' 23:59:59';
                break;

            case 'Last 3 Days':
                const last3Days = new Date(new Date().setDate(new Date().getDate() - 3));
                fromTime = last3Days.toISOString().split('T')[0] + ' 00:00:00';
                toTime = new Date().toISOString().split('T')[0] + ' 23:59:59';
                break;
        }

        if (handleDuration && fromTime && toTime) handleDuration(duration, { fromTime, toTime });
        setAnchorEl(null);
    };

    const handleCustomMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setCustomAnchorEl(event.currentTarget);
    };

    const handleCustomMenuClose = (event: any) => {
        setCustomAnchorEl(null);
        if (!!event) {
            setAnchorEl(null);
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        if (event.currentTarget) setAnchorEl(event.currentTarget);
    };

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnackbar(false);
    };

    useEffect(() => {
        handleSelectDuration(duration);
        setEndTime(dayjs().tz(localStorage.getItem('userTZ') || ''));
    }, []);

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <div>
                    <div onClick={handleMenuOpen}>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 400,
                                fontSize: '12px',
                                textAlign: 'start',
                                color: '#536580',
                                cursor: 'pointer',
                                display: 'inline-block'
                            }}
                        >
                            <Fragment>{duration}</Fragment>
                        </Typography>
                        <KeyboardArrowDownOutlinedIcon style={{ color: '#536580', fontSize: '10px' }} />
                    </div>
                    <Menu
                        sx={{ width: '50px' }}
                        anchorEl={anchorEl}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right'
                        }}
                        keepMounted
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right'
                        }}
                        open={open}
                        onClose={() => {
                            handleDuration && handleDuration();
                            setAnchorEl(null);
                        }}
                        PaperProps={{ className: classes.dropdownMenu }}
                    >
                        {!allowSingleCustomDate && (<>
                            {enableTodayDuration && (
                                <Box className={classes.timePickerMenu}>
                                    <Grid item display={'flex'} className={classes.alignAccestime}>
                                        <AccessTime style={{ color: '#3D8BF8' }} />
                                        <Typography className={classes.todayTypography} flexGrow={1}> Today</Typography>
                                        {showRealTimeToggle ? <Box display={'flex'} alignItems={'center'} pl={1} pr={3}>
                                            <Typography sx={{ fontSize: '12px', fontWeight: 400, color: '#536580', marginRight: '10px' }}> Real Time</Typography>
                                            <SimpleSwitch id="realtime-toggle" name="realtime-toggle" checked={realtime} onChange={handleClickRealTime} />
                                            <StyledTooltip title="Data will be provided from 00:00:00 to current time.">
                                                <Info size={16} style={{ margin: '0px 6px' }} color="#3D8BF8" />
                                            </StyledTooltip>
                                        </Box> : null}
                                    </Grid>
                                    <Grid container flexDirection="row" justifyContent="center" alignContent="center" gap={1}>
                                        <Grid item xs={4}>
                                            <TimePicker disabled={realtime} value={startTime} onChange={onStartTimeChange} defaultValue={startTime} minuteStep={5} secondStep={5} />
                                        </Grid>
                                        <Grid item xs={2} className={classes.arrowIconContainer} borderRadius={1}>
                                            <img src={`${process.env.PUBLIC_URL}/icons/time-side-arrow.svg`} alt="arrow" />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TimePicker disabled={realtime} value={endTime} onChange={onEndTimeChange} defaultValue={endTime} minuteStep={5} secondStep={5} />
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                            {enableLastThreeDaysDuration && (
                                <MenuItem className={classes.dropdownMenuItem} onClick={() => handleSelectDuration('Last 3 Days')}>
                                    Last 3 Days
                                </MenuItem>
                            )}
                            {(!enableTodayDuration && !enableToday) && (
                                <MenuItem className={classes.dropdownMenuItem} onClick={() => handleSelectDuration('Yesterday')}>
                                    Yesterday
                                </MenuItem>
                            )}
                            {enableToday && (
                                <MenuItem className={classes.dropdownMenuItem} onClick={() => handleSelectDuration('Today')}>
                                    Today
                                </MenuItem>
                            )}
                            {enableSameDayLastWeek && (
                                <MenuItem className={classes.dropdownMenuItem} onClick={() => handleSelectDuration('Same day last week')}>
                                    Same day last week
                                </MenuItem>
                            )}
                            {!enableTodayDuration && (
                                <MenuItem className={classes.dropdownMenuItem} onClick={() => handleSelectDuration('Last 7 Days')}>
                                    Last 7 Days
                                </MenuItem>
                            )}
                            {!enableTodayDuration && (
                                <div>
                                    <MenuItem className={classes.dropdownMenuItem} onClick={handleCustomMenuClick}>
                                        Custom
                                    </MenuItem>
                                    <Menu
                                        id="demo-positioned-menu"
                                        aria-labelledby="demo-positioned-button"
                                        anchorEl={customAnchorEl}
                                        open={customOpen}
                                        onClose={handleCustomMenuClose}
                                        anchorOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left'
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left'
                                        }}
                                        sx={{ width: '376px', padding: '0px' }}
                                    >
                                        <CustomDatePicker
                                            handleDuration={handleDuration}
                                            handleClose={handleCustomMenuClose}
                                            withTime={withTimePicker}
                                            allowDateTillDay={allowTodayDateInCustomDate}
                                            allowFutureDates={allowFutureDates}
                                            allowSingleDate={false}
                                        />
                                    </Menu>
                                </div>
                            )}
                        </>)}
                        {allowSingleCustomDate && (
                            <Box className={classes.singleDatePickerMenu}>
                                <CustomDatePicker
                                    handleDuration={handleDuration}
                                    handleClose={handleCustomMenuClose}
                                    withTime={withTimePicker}
                                    allowDateTillDay={allowTodayDateInCustomDate}
                                    allowFutureDates={allowFutureDates}
                                    allowSingleDate={allowSingleCustomDate}
                                />
                            </Box>
                        )}
                    </Menu>
                </div>
            </div>
            <Snackbar open={openSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} autoHideDuration={3000} onClose={handleClose}>
                <Alert
                    iconMapping={{
                        error: <DoDisturbOnIcon style={{ color: 'red' }} />
                    }}
                    onClose={handleClose}
                    severity="error"
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default DurationSelector; 